import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import moment from 'moment';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LocalStorageUtil } from '../../../../../core/utils/local-storage-util';
import { DateUtil } from '../../../../../core/utils/date-util';
import { HandoverNotesService } from '../../../../../state/modules/handover-notes/handover-notes.service';
import { HANDOVER_NOTES_DEFAULT_ACTIVE_PAGGING_PAYLOAD, HANDOVER_NOTES_DEFAULT_COMPLETE_PAGGING_PAYLOAD, HANDOVER_NOTES_TYPE } from '../../pages/handover-notes/handover-notes.constant';
import { COMPLETED_HANDOVER_NOTES_FILTER_CONTROL, HANDOVER_NOTES_FILTER_CONTROL } from './handover-notes-list.constant';
import { MasterService } from '../../../../../state/master/services/master.service';

@Component({
  standalone: false,
  selector: 'kt-handover-notes-list',
  templateUrl: './handover-notes-list.component.html',
  styleUrls: ['./handover-notes-list.component.scss'],
})
export class HandoverNotesListComponent implements OnInit, AfterViewInit, OnDestroy {

  loading = signal(false);
  _activeTab;
  lastPayload;
  paggingPayload;
  shiftDetail;
  handoverListData = signal<any[]>([]);
  sortingPayload = [
    {
      field: 'hand_over_note_modified_at',
      dir: 'desc',
    },
  ];
  userId;

  @Input() set _userId(data) {
    if(data) {
      this.userId = data;
    } else {
      this.userId = LocalStorageUtil.getUserId();
    }
  }

  @Input() type;

  @Input() set _shiftDetail(data) {
    if (data) {
      this.shiftDetail = data;
    }
  }

  @Input() set activeTab(tab) {
    this._activeTab = tab;
  }

  @Input() set tabChanged(data) {
    if (data) {
      this.resetPaggingPayload();
      this.lastPayload.sort = this.sortingPayload;
      setTimeout(() => {
        this.getAllHandoverNotes(this.form.value, false);
      }, 1);
    }
  };

  form: any = new UntypedFormGroup({});
  formControls = HANDOVER_NOTES_FILTER_CONTROL;
  lookup = {
    client: new BehaviorSubject<any[]>([]),
    location: new BehaviorSubject<any[]>([]),
  };

  filterData;

  @Input() set _sort(data) {
    if (data) {
      this.sortingPayload = data;
      this.resetPaggingPayload();
      this.getAllHandoverNotes(this.form.value, false);
    }
  };

  scrolledHandoverList = false;
  subscriptions: Subscription[] = [];

  dayLimit = Number.POSITIVE_INFINITY;
  totalCount = 0;

  constructor(public handoverNotesService: HandoverNotesService, private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.getClientLookup();
    this.subscriptions.push(this.handoverNotesService.reload.subscribe(response => {
      if (response && response['type'] === this._activeTab) {
        this.resetPaggingPayload();
        this.getAllHandoverNotes({ ...this.form.value }, false);
      }
    }));
  }

  ngAfterViewInit(): void {
    this.form.get('search').valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe(res => {
      this.resetPaggingPayload();
      this.getAllHandoverNotes({ ...this.form.value }, false);
    });
  }

  /**
   * To get Client List assigned to particular user
   */
  getClientLookup() {
    this.subscriptions.push(this.handoverNotesService.getClientLookup(this.userId).subscribe((data: any) => {
      if (data && data.data && data.data.data && data.data.data.clients) {
        this.lookup.client.next([{ client_id: 0, client_name: 'All ' + MasterService.masterBusinessLabel.Client }, ...data.data.data.clients]);
      }
      if (this.shiftDetail && this.shiftDetail.location && this.shiftDetail.location.location_id) {
        this.getLocationLookup(this.shiftDetail.client.client_id);
      }
      this.loading.set(false);
      // lookup.client is reassigned inside an HTTP subscribe; force CD so the
      // new options reach kt-select's ngOnChanges (it reads [options] live).
      this.cdr.detectChanges();
    }, err => {
      this.loading.set(false);
    }));
  }

  /**
   * To get available location data
   * @param data It will have clientId
   */
  getLocationLookup(clientId) {
    const payload = {
      clientId: clientId,
      userId: this.userId
    };

    this.subscriptions.push(this.handoverNotesService.getLocationLookup(payload).subscribe((data: any) => {
      if (data && data.data && data.data.data && data.data.data.location) {
        this.lookup.location.next([{ location_id: 0, location_name: 'All ' +  MasterService.masterBusinessLabel.Location }, ...data.data.data.location]);
      }
      this.loading.set(false);
      // Same as client lookup: reassigned in a subscribe, force CD to render.
      this.cdr.detectChanges();
    }, err => {
      this.loading.set(false);
    }));
  }

  /**
   * Reset payload to default state
   */
  resetPaggingPayload() {
    this.lastPayload = this._activeTab === HANDOVER_NOTES_TYPE.Active ? { ...HANDOVER_NOTES_DEFAULT_ACTIVE_PAGGING_PAYLOAD } : { ...HANDOVER_NOTES_DEFAULT_COMPLETE_PAGGING_PAYLOAD };
    this.lastPayload.sort = this.sortingPayload;
  }

  /**
   * Called when user clicks on search button in Active Handover notes
   */
  searchClick() {
    this.resetPaggingPayload();
    const payload = { ...this.form.value, type: this._activeTab };
    this.getAllHandoverNotes(payload, false);
  }

  refreshData() {
    const payload = { ...this.form.value, type: this._activeTab };
    this.getAllHandoverNotes(payload, false);
  }

  /**
   * Called when user changes Client Filter value in Active Handover notes
   */
  clientChange(event) {
    if (event && event.name && event.name === 'clientIds') {
      this.form.get('locationIds').reset();
      this.lookup.location.next([]);
      if (event.value === 0) {
        this.lookup.location.next([{ location_id: 0, location_name: 'All Locations' }]);
      } else if (event.value) {
        this.getLocationLookup(event.value);
      }
    }
  }

  /**
   * Paging Api call(Handover notes list data)
   * @param data It is user's active or scheduled shift data
   */
  getAllHandoverNotes(data = null, append = true) {
    this.filterData = data;
    this.loading.set(true);
    let payload = {};

    if (data) {
      payload = this.preparePayloadFromPagging(data);
    } else {
      payload = {
        type: this.activeTab,
        // min_date: DateUtil.ngbDateToUtc(data['date-range_start']),
        // max_date: DateUtil.ngbDateToUtc(data['date-range_end']),
      };
    }

    this.fetchAllNotes(payload, append);
  }

  /**
   * Fetch all the notes
   */
  fetchAllNotes(payload, append) {
    this.subscriptions.push(this.handoverNotesService.handoverNotesPaging(payload).subscribe((response: any) => {
      if (response && response.data && response.data.data) {
        if (append) {
          this.handoverListData.set([...this.handoverListData(), ...response.data.data.hand_over_note_comment]);
        } else {
          this.handoverListData.set([...response.data.data.hand_over_note_comment]);
        }
        this.totalCount = response.data.data.TotalCount;
      }
      this.loading.set(false);
    }, err => {
      this.loading.set(false);
    }));
  }

  /**
   * Prepare payload for pagging API
   */
  preparePayloadFromPagging(data, orderBy = null) {
    const payload: any = {};

    if (this.lastPayload && this.lastPayload.sort && this.lastPayload.sort.length) {
      payload.sort = this.lastPayload.sort;
    }
    if (this.lastPayload && this.lastPayload.page_number) {
      payload.page_number = this.lastPayload.page_number;
      payload.page_size = this.lastPayload.page_size;
    }

    payload.type = this._activeTab;

    if (data && data.client && data.client.client_id && data.location && data.location.location_id) {
      payload.client_id = data.client.client_id;
      payload.location_id = data.location.location_id;
    }
    if (data && data.clientIds && typeof (data.clientIds) === 'number') {
      payload.client_id = data.clientIds;
    }
    if (data && data.locationIds && typeof (data.locationIds) === 'number') {
      payload.location_id = data.locationIds;
    }
    if (data && data.search) {
      payload.global_filter_value = data.search;
      payload.global_fields = [{
        field: 'hand_over_note_text',
        type: 'string',
      }];
    }
    if (data && data['date-range_start'] && data['date-range_end']) {
      payload.min_date = DateUtil.ngbDateToUtc(data['date-range_start']);
      payload.max_date = DateUtil.ngbDateToUtc(data['date-range_end']);
    }
    if (data && data.page_number && data.page_size) {
      // if (data && data.page_number && data.page_size && !orderBy) {
      payload.page_number = data.page_number;
      payload.page_size = data.page_size;
      this.scrolledHandoverList = true;
    }
    payload.user_id = this.userId;
    return payload;
  }

  /**
   * Handle scrolling event for infinite scrolling functionality
   */
  scrolled() {
    if (this.totalCount > this.handoverListData().length) {
      this.lastPayload.page_number += 1;
      this.getAllHandoverNotes({ ...this.form.value }, true);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

}
